import { useAccountingPageLazyQuery } from '../../../../services';
import {
    DBlock,
    DMonth,
    DPage,
    DYear,
    useDMonth,
    useDYear,
} from '../../../dum';
import React, { useEffect, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { BUSINESS_TZ, formatFloat, isNumber } from '../../../../helpers';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import { AccountingActionsMenu } from './accounting-actions-menu';
import { AccountingPendingPanel } from './accounting-pending-panel';
import { AccountingTabs } from './accounting-tabs';
import {
    AccountingWorkSheets,
    AccountPage,
    buildAccountingExport,
    ExpenseRow,
    OrderSaleRow,
    pendingCounts,
    pendingExpenses,
    pendingLedgerRows,
    pendingOrderSales,
    TransferDetail,
} from './accounting-export-rules';

export function Accounting() {
    const businessYear = dayjs().tz(BUSINESS_TZ).year();
    const businessMonth = dayjs().tz(BUSINESS_TZ).month();
    const { year, setYear } = useDYear({
        COMPONENT_KEY: Accounting.name + '_Year',
        defaultValue: businessYear,
    });

    const { month, setMonth } = useDMonth({
        COMPONENT_KEY: Accounting.name + '_Month',
        defaultValue: businessMonth,
    });

    const isYearNumber = useMemo(() => isNumber(year), [year]);
    const isMonthNumber = useMemo(() => isNumber(month), [month]);

    const hasPeriodSelected = year !== null && month !== null;

    const [query, { data, loading, refetch }] = useAccountingPageLazyQuery();

    useEffect(() => {
        if (year === null || month === null) return;
        const businessToday = dayjs().tz(BUSINESS_TZ);
        const startDate = businessToday.year(year).month(month).startOf('month').format('YYYY-MM-DD');
        const endDate = businessToday.year(year).month(month).add(1, 'month').startOf('month').format('YYYY-MM-DD');
        void query({
            variables: {
                startDate,
                endDate,
            },
        });
    }, [query, month, year]);

    const monthLabel = useMemo(() => {
        if (!isYearNumber) return '';
        if (isMonthNumber) {
            return dayjs().tz(BUSINESS_TZ).year(year!).month(month!).locale('es').format('MMMM YYYY');
        }
        return String(year);
    }, [month, year, isMonthNumber, isYearNumber]);

    const { workSheets, exportWorkSheets } = useMemo(() => {
        const accounts =
            data?.getAccounts.filter((account) => {
                return account.is_own;
            }) || [];

        const excludedExportAccountIds = new Set(
            (data?.getAccounts ?? [])
                // Informal accounts (efectivo / notas) are excluded from the
                // formal accountability export — the exclusion derives from the flag.
                .filter((account) => account.is_informal_account)
                .map((account) => account.id),
        );
        const receiptTypes = data?.getReceiptTypes ?? [];
        const includedReceiptTypeIds = new Set(
            receiptTypes
                // The formal (fiscal) receipt types are the non-informal ones.
                .filter((rt) => !rt.is_informal_receipt)
                .map((rt) => rt.id),
        );

        const accountsPages: AccountPage[] = [];

        accounts.forEach((account) => {
            const accountPageData: AccountPage['data'] = [];
            data?.getTransfers
                .filter((transfer) => {
                    return (
                        transfer.to_account?.id === account.id ||
                        transfer.from_account?.id === account.id
                    );
                })
                .forEach((transfer, transferIndex) => {
                    const fromAccount = transfer.from_account;
                    const toAccount = transfer.to_account;
                    const transferredDate = transfer.transferred_date ?? null;
                    const transferredAmount = transfer.amount;

                    if (transfer.transfer_receipts.length > 0) {
                        transfer.transfer_receipts.forEach(
                            (transferReceipt, index) => {
                                const orderSale = transferReceipt.order_sale;
                                const expense = transferReceipt.expense;
                                const documentAccount = orderSale
                                    ? [fromAccount, toAccount].find(
                                          (account) =>
                                              account?.is_client &&
                                              !account.is_own,
                                      )
                                    : expense
                                    ? [fromAccount, toAccount].find(
                                          (account) =>
                                              account?.is_supplier &&
                                              !account.is_own,
                                      )
                                    : null;

                                const orderCode =
                                    orderSale?.invoice_code ||
                                    expense?.external_code ||
                                    '';
                                const receiptIssueDate =
                                    orderSale?.date ?? expense?.date ?? null;
                                const receiptTotal =
                                    orderSale?.total_with_tax ||
                                    expense?.total_with_tax ||
                                    0;
                                const receiptTax =
                                    orderSale?.tax || expense?.tax || 0;
                                const taxRetained = expense?.tax_retained || 0;
                                const nonTaxRetained =
                                    expense?.non_tax_retained || 0;

                                const notes: string =
                                    orderSale?.notes || expense?.notes || '';

                                const supplement: string =
                                    expense?.require_supplement
                                        ? expense?.supplement_code
                                        : orderSale?.require_supplement
                                        ? orderSale?.supplement_code
                                        : '-';
                                const creditNote: string =
                                    orderSale?.require_credit_note
                                        ? `${
                                              orderSale.credit_note_code
                                          } (${formatFloat(
                                              orderSale.credit_note_amount,
                                          )})`
                                        : '-';
                                accountPageData.push({
                                    transferIndex: transferIndex + 1,
                                    transferTypeId:
                                        transfer.transfer_type_id || 0,
                                    transferType: fromAccount?.is_own
                                        ? 'Cargo'
                                        : 'Abono',
                                    fromAccountName: fromAccount?.name || '',
                                    toAccountName: toAccount?.name || '',
                                    transferDate: transferredDate,
                                    orderCode: orderCode,
                                    receiptIssueDate: receiptIssueDate,
                                    // One transfer can settle several receipts;
                                    // only the first row carries the transferred
                                    // amount so column totals stay correct.
                                    transferredAmount:
                                        index === 0 ? transferredAmount : null,
                                    receiptTotal: receiptTotal,
                                    receiptTax: receiptTax,
                                    taxRetained: taxRetained,
                                    nonTaxRetained: nonTaxRetained,
                                    notes: notes,
                                    supplement: supplement,
                                    creditNote: creditNote,
                                    documentCanceled: !!(
                                        orderSale?.canceled || expense?.canceled
                                    ),
                                    missingFolio: !!(
                                        (orderSale?.require_invoice_code && !orderSale.invoice_code) ||
                                        (expense?.require_external_code && expense.external_code === '')
                                    ),
                                    missingSupplement: !!(
                                        (expense?.require_supplement && expense.supplement_code === '') ||
                                        (orderSale?.require_supplement && orderSale.supplement_code === '')
                                    ),
                                    missingCreditNote: !!(
                                        orderSale?.require_credit_note && orderSale.credit_note_code === ''
                                    ),
                                    missingTax: !!(
                                        (orderSale?.require_tax ||
                                            expense?.require_tax) &&
                                        receiptTax === 0
                                    ),
                                    orderSaleId: orderSale?.id ?? null,
                                    expenseId: expense?.id ?? null,
                                    documentIdentifier: orderSale
                                        ? String(
                                              orderSale.invoice_code ||
                                                  orderSale.order_code,
                                          )
                                        : expense?.require_external_code &&
                                          expense.external_code !== ''
                                        ? expense.external_code
                                        : '',
                                    documentAccountName:
                                        documentAccount?.name ??
                                        (orderSale
                                            ? fromAccount?.name
                                            : toAccount?.name) ??
                                        '',
                                    documentTransferAmount:
                                        transferReceipt.amount,
                                });
                            },
                        );
                    } else {
                        accountPageData.push({
                            transferIndex: transferIndex + 1,
                            transferTypeId: transfer.transfer_type_id || 0,
                            transferType: 'Traspaso entre cuentas',
                            fromAccountName: fromAccount?.name || '',
                            toAccountName: toAccount?.name || '',
                            transferDate: transferredDate,
                            orderCode: '',
                            receiptIssueDate: null,
                            transferredAmount: transferredAmount,
                            receiptTotal: null,
                            receiptTax: null,
                            taxRetained: null,
                            nonTaxRetained: null,
                            notes: '',
                            supplement: '-',
                            creditNote: '-',
                            documentCanceled: false,
                            missingFolio: false,
                            missingSupplement: false,
                            missingCreditNote: false,
                            missingTax: false,
                            orderSaleId: null,
                            expenseId: null,
                            documentIdentifier: '',
                            documentAccountName: '',
                            documentTransferAmount: 0,
                        });
                    }
                });
            accountsPages.push({
                accountId: account.id,
                accountName: account.name,
                excludeFromAccountabilityExport: !!account.is_informal_account,
                data: accountPageData,
            });
        });

        const orderSales = data?.getOrderSales;

        const orderSalesPage: OrderSaleRow[] = [];
        const canceledOrderSalesPage: OrderSaleRow[] = [];

        const buildOrderSaleRow = (
            orderSale: NonNullable<typeof orderSales>[number],
        ): OrderSaleRow => ({
            id: orderSale.id,
            orderCode: orderSale.order_code,
            requiresInvoiceCode: orderSale.require_invoice_code,
            // `invoice_code` uses 0 as its empty database value. Normalize it
            // to the same blank display value used by expense.external_code.
            invoiceCode: orderSale.invoice_code
                ? String(orderSale.invoice_code)
                : '',
            date: orderSale.date ?? null,
            accountName: orderSale?.account?.name || '',
            total: orderSale.total_with_tax,
            tax: orderSale.tax,
            transferReceiptsTotal: orderSale.transfer_receipts_total,
            transfers: orderSale.transfer_receipts.map(
                (transferReceipt): TransferDetail => ({
                    accountAbbreviation:
                        transferReceipt.transfer?.to_account?.abbreviation ??
                        '',
                    date: transferReceipt.transfer?.transferred_date ?? null,
                    amount: transferReceipt.amount,
                }),
            ),
            supplement: orderSale.require_supplement
                ? orderSale.supplement_code === ''
                    ? 'PENDIENTE'
                    : orderSale.supplement_code
                : '-',
            creditNote: orderSale.require_credit_note
                ? `${
                      orderSale.credit_note_code === ''
                          ? 'PENDIENTE'
                          : orderSale.credit_note_code
                  } ($${formatFloat(orderSale.credit_note_amount)})`
                : '-',
            notes: orderSale.notes,
            includeInAccountabilityExport: includedReceiptTypeIds.has(
                orderSale.receipt_type_id ?? 0,
            ),
            missingInvoiceCode:
                orderSale.require_invoice_code && !orderSale.invoice_code,
            missingSupplement:
                orderSale.require_supplement && orderSale.supplement_code === '',
            missingCreditNote:
                orderSale.require_credit_note &&
                orderSale.credit_note_code === '',
            missingTax: orderSale.require_tax && orderSale.tax === 0,
        });

        orderSales?.forEach((orderSale) => {
            if (orderSale.canceled) {
                canceledOrderSalesPage.push(buildOrderSaleRow(orderSale));
            } else {
                orderSalesPage.push(buildOrderSaleRow(orderSale));
            }
        });

        const expenses = data?.getExpenses;

        // Active and canceled expenses come from one list, so they share one row
        // builder (the sales side already does this with `buildOrderSaleRow`).
        const buildExpenseRow = (
            expense: NonNullable<typeof expenses>[number],
        ): ExpenseRow => ({
            id: expense.id,
            requiresExternalCode: expense.require_external_code,
            orderCode: expense.external_code,
            date: expense.date ?? null,
            excludeFromAccountabilityExport: expense?.account?.id
                ? excludedExportAccountIds.has(expense.account.id)
                : false,
            includeInAccountabilityExport: includedReceiptTypeIds.has(
                expense.receipt_type_id ?? 0,
            ),
            paidWithNotasAccount: expense.transfer_receipts.some(
                (tr) =>
                    (tr.transfer?.from_account?.id != null &&
                        excludedExportAccountIds.has(
                            tr.transfer.from_account.id,
                        )) ||
                    (tr.transfer?.to_account?.id != null &&
                        excludedExportAccountIds.has(tr.transfer.to_account.id)),
            ),
            accountName: expense?.account?.name || '',
            tax: expense.tax,
            taxRetained: expense.tax_retained,
            nonTaxRetained: expense.non_tax_retained,
            totalWithTax: expense.total_with_tax,
            transferReceiptsTotal: expense.transfer_receipts_total,
            transfers: expense.transfer_receipts.map(
                (transferReceipt): TransferDetail => ({
                    accountAbbreviation:
                        transferReceipt.transfer?.from_account?.abbreviation ??
                        '',
                    date: transferReceipt.transfer?.transferred_date ?? null,
                    amount: transferReceipt.amount,
                }),
            ),
            supplement: expense.require_supplement
                ? expense.supplement_code === ''
                    ? 'PENDIENTE'
                    : expense.supplement_code
                : '-',
            notes: expense.notes,
            // Sales and expenses both carry explicit per-document requirement
            // flags; the stored tax value never decides whether a folio is due.
            missingExternalCode:
                expense.require_external_code && expense.external_code === '',
            missingSupplement:
                expense.require_supplement && expense.supplement_code === '',
            missingTax: expense.require_tax && expense.tax === 0,
        });

        // The worksheet a row lands on IS its canceled status — no row carries a
        // `canceled` field, so a canceled document cannot reach an active sheet
        // or total by accident. `getExpenses` returns canceled rows too when
        // `is_canceled` is not passed, so one call feeds both pages, exactly as
        // `getOrderSales` does above.
        const expensePage: ExpenseRow[] = (expenses ?? [])
            .filter((expense) => !expense.canceled)
            .map(buildExpenseRow);
        const canceledExpensePage: ExpenseRow[] = (expenses ?? [])
            .filter((expense) => expense.canceled)
            .map(buildExpenseRow);

        const workSheets: AccountingWorkSheets = {
            accountsPages: accountsPages,
            orderSalePage: orderSalesPage,
            canceledOrderSalePage: canceledOrderSalesPage,
            expensePage: expensePage,
            canceledExpensePage: canceledExpensePage,
        };

        // The tabs keep every row and mark the excluded ones; everything that
        // produces a number — tiles, workbook, pendientes — reads the export set
        // instead, so the screen and the workbook can no longer drift apart.
        return {
            workSheets,
            exportWorkSheets: buildAccountingExport(workSheets),
        };
    }, [data]);

    const pending = useMemo(() => {
        const bank = pendingLedgerRows(exportWorkSheets.accountsPages);
        const sales = pendingOrderSales(exportWorkSheets.orderSalePage);
        const expenses = pendingExpenses(exportWorkSheets.expensePage);
        return {
            bankSales: bank.sales,
            bankExpenses: bank.expenses,
            sales,
            expenses,
            bankSalesCounts: pendingCounts(bank.sales),
            bankExpensesCounts: pendingCounts(bank.expenses),
        };
    }, [exportWorkSheets]);

    return (
        <DPage>
            <DBlock>
                {/* ── Filters row ── */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 4, gap: 2 }}>
                    <Box sx={{ mr: 2 }}>
                        <DYear setYear={setYear} year={year} />
                    </Box>
                    <Box sx={{ mr: 2 }}>
                        <DMonth setMonth={setMonth} month={month} />
                    </Box>
                    <Box sx={{ ml: 'auto' }}>
                        <AccountingActionsMenu
                            exportWorksheets={exportWorkSheets}
                            pendingBankSales={pending.bankSales}
                            pendingBankExpenses={pending.bankExpenses}
                            pendingSales={pending.sales}
                            pendingExpenses={pending.expenses}
                            disabled={!isYearNumber || loading || !data}
                            monthLabel={monthLabel}
                        />
                    </Box>
                </Box>

                {!hasPeriodSelected ? (
                    <Box
                        sx={{
                            flex: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            py: 8,
                        }}
                    >
                        <Typography variant={'h6'} sx={{ color: 'text.secondary' }}>
                            Selecciona un año y un mes para ver la información.
                        </Typography>
                    </Box>
                ) : (
                <>
                {/* ── Pendientes ── */}
                <Box sx={{ mb: 3 }}>
                    <AccountingPendingPanel
                        loading={loading}
                        bankSalesCounts={pending.bankSalesCounts}
                        bankExpensesCounts={pending.bankExpensesCounts}
                    />
                </Box>

                {/* ── Tabs ── */}
                <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                    <AccountingTabs
                        worksheets={workSheets}
                        loading={loading}
                        onEntityEdited={() => {
                            void refetch?.();
                        }}
                    />
                </Box>
                </>
                )}
            </DBlock>
        </DPage>
    );
}
